
import React, { useState, useEffect } from 'react';
import { socialCommerceService } from '../services/socialCommerceService';
import { auctionService } from '../services/auctionService';
import { generateVideoScript } from '../services/geminiService';
import { storage } from '../lib/storage';
import { SocialVideo, Product, AuctionEvent } from '../types';
import { 
  Film, 
  Plus, 
  Trash2, 
  Play, 
  Eye, 
  Sparkles, 
  TrendingUp, 
  Loader2, 
  X, 
  Check, 
  LayoutGrid, 
  BarChart3, 
  Video, 
  ArrowRight,
  Zap,
  MessageSquareQuote,
  Hammer,
  Clock,
  Calendar,
  Timer
} from 'lucide-react';

interface Props {
  storeId: string;
}

const AdminSocialHub: React.FC<Props> = ({ storeId }) => {
  const [videos, setVideos] = useState<SocialVideo[]>([]);
  const [auctions, setAuctions] = useState<AuctionEvent[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<'reels' | 'auctions'>('reels');
  const [showModal, setShowModal] = useState(false);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Script Gen State
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [generatedScript, setGeneratedScript] = useState<any>(null);

  const [formData, setFormData] = useState({
    productId: '',
    videoUrl: '',
    thumbnailUrl: '',
    caption: ''
  });

  const [auctionFormData, setAuctionFormData] = useState({
    productId: '',
    startingPrice: 0,
    bidIncrement: 10,
    durationMinutes: 60,
    videoUrl: ''
  });

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = () => {
    setIsLoading(true);
    setVideos(socialCommerceService.getStoreVideos(storeId));
    setAuctions(auctionService.getStoreAuctions(storeId));
    setProducts(storage.getProducts().filter(p => p.storeId === storeId));
    setIsLoading(false);
  };

  const handleGenerateScript = async () => {
    if (!selectedProduct) return alert("Bhai, product toh choose karo script ke liye!");
    setIsGeneratingScript(true);
    const script = await generateVideoScript(selectedProduct);
    setGeneratedScript(script);
    setIsGeneratingScript(false);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.productId || !formData.videoUrl) return alert("Details bharo bhai!");
    
    socialCommerceService.saveVideo({
      ...formData,
      id: 'vid-' + Date.now(),
      storeId,
      views: 0,
      likes: 0,
      shares: 0,
      conversionRate: 0,
      status: 'ACTIVE'
    });
    
    setShowModal(false);
    loadData();
    setFormData({ productId: '', videoUrl: '', thumbnailUrl: '', caption: '' });
  };

  const handleCreateAuction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!auctionFormData.productId) return alert("Product select karo!");

    const now = new Date();
    const end = new Date(now.getTime() + auctionFormData.durationMinutes * 60000);

    auctionService.saveAuction({
      id: 'auc-' + Date.now(),
      storeId,
      productId: auctionFormData.productId,
      startTime: now.toISOString(),
      endTime: end.toISOString(),
      startingPrice: Number(auctionFormData.startingPrice),
      currentPrice: Number(auctionFormData.startingPrice),
      bidIncrement: Number(auctionFormData.bidIncrement),
      bids: [],
      status: 'ACTIVE',
      videoUrl: auctionFormData.videoUrl
    });

    setShowAuctionModal(false);
    loadData();
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">Social Commerce Hub</h1>
          <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Viral & Live Selling Strategy</p>
        </div>
        <div className="flex gap-4">
           <button 
             onClick={() => setShowAuctionModal(true)}
             className="bg-red-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-red-100 hover:bg-red-700 transition-all flex items-center gap-2"
           >
             <Hammer className="h-4 w-4" /> Start Auction
           </button>
           <button 
             onClick={() => setShowModal(true)}
             className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2"
           >
             <Plus className="h-4 w-4" /> Upload Reel
           </button>
        </div>
      </div>

      <div className="flex gap-4 p-1.5 bg-white rounded-2xl border border-gray-100 w-fit shadow-sm">
         <button onClick={() => setActiveTab('reels')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase transition-all ${activeTab === 'reels' ? 'bg-indigo-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}>Viral Reels</button>
         <button onClick={() => setActiveTab('auctions')} className={`px-6 py-3 rounded-xl text-xs font-black uppercase transition-all ${activeTab === 'auctions' ? 'bg-red-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}>Live Auctions</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
         <div className="lg:col-span-2 space-y-6">
            {activeTab === 'reels' ? (
              videos.length === 0 ? (
                <div className="bg-white p-24 text-center space-y-4 rounded-[40px] border border-dashed border-gray-200 opacity-50">
                   <Video className="h-16 w-16 mx-auto text-gray-300" />
                   <p className="font-black uppercase tracking-widest text-xs">No videos uploaded yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   {videos.map(vid => (
                    <div key={vid.id} className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden group hover:border-indigo-200 transition-all">
                       <div className="aspect-video relative bg-gray-900 overflow-hidden">
                          <img src={vid.thumbnailUrl} className="w-full h-full object-cover opacity-60" />
                          <div className="absolute inset-0 flex items-center justify-center">
                             <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center border border-white/30"><Play className="h-5 w-5 text-white fill-current" /></div>
                          </div>
                       </div>
                       <div className="p-6">
                          <p className="font-black text-gray-900 truncate">{vid.caption}</p>
                          <div className="flex justify-between items-center mt-4">
                             <span className="text-[10px] text-gray-400 font-bold">❤️ {vid.likes} Likes</span>
                             <button onClick={() => { socialCommerceService.deleteVideo(vid.id); loadData(); }} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>
                          </div>
                       </div>
                    </div>
                   ))}
                </div>
              )
            ) : (
              auctions.length === 0 ? (
                <div className="bg-white p-24 text-center space-y-4 rounded-[40px] border border-dashed border-gray-200 opacity-50">
                   <Hammer className="h-16 w-16 mx-auto text-gray-300" />
                   <p className="font-black uppercase tracking-widest text-xs">No auctions recorded. Host a live event to spike revenue!</p>
                </div>
              ) : (
                <div className="space-y-6">
                   {auctions.map(auc => {
                      const p = products.find(prod => prod.id === auc.productId);
                      return (
                        <div key={auc.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl flex flex-col md:flex-row justify-between items-center gap-6 group hover:border-red-200 transition-all">
                           <div className="flex items-center gap-6">
                              <div className="w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center">
                                 <Hammer className="h-8 w-8" />
                              </div>
                              <div>
                                 <div className="flex items-center gap-3">
                                    <h3 className="text-xl font-black text-gray-900">{p?.name}</h3>
                                    <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase border ${auc.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600 border-emerald-100 animate-pulse' : 'bg-gray-100 text-gray-400'}`}>
                                       {auc.status}
                                    </span>
                                 </div>
                                 <p className="text-xs font-bold text-gray-400 mt-1 uppercase">Ends: {new Date(auc.endTime).toLocaleTimeString()}</p>
                                 <div className="flex items-center gap-4 mt-3">
                                    <span className="text-[10px] font-black text-gray-900 uppercase">Bids: {auc.bids.length}</span>
                                    <span className="text-[10px] font-black text-emerald-600 uppercase">Current: ${auc.currentPrice}</span>
                                 </div>
                              </div>
                           </div>
                        </div>
                      );
                   })}
                </div>
              )
            )}
         </div>

         <div className="space-y-8">
            <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl space-y-8 relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-8 opacity-20 group-hover:scale-110 transition-transform"><Sparkles className="h-24 w-24" /></div>
               <div className="relative space-y-6">
                  <h3 className="text-xl font-black tracking-tight">AI Viral Intel</h3>
                  <p className="text-indigo-100 text-sm leading-relaxed">
                     Bhai, data shows auctions with a duration of <span className="text-white font-black underline">30 minutes</span> have the highest "sniping" activity and revenue.
                  </p>
                  <button className="w-full py-4 bg-white text-indigo-600 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl">Optimize Next Event</button>
               </div>
            </div>
         </div>
      </div>

      {/* Upload Modal (Existing) */}
      {showModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">Upload Viral Reel</h3>
                 <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Link to Product</label>
                    <select required value={formData.productId} onChange={e => setFormData({...formData, productId: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold">
                       <option value="">Select a product...</option>
                       {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input required value={formData.videoUrl} onChange={e => setFormData({...formData, videoUrl: e.target.value})} placeholder="Video URL" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    <input required value={formData.thumbnailUrl} onChange={e => setFormData({...formData, thumbnailUrl: e.target.value})} placeholder="Thumbnail URL" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <textarea value={formData.caption} onChange={e => setFormData({...formData, caption: e.target.value})} placeholder="Caption..." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" rows={2} />
                 <button type="submit" className="w-full py-6 bg-indigo-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">Publish Reel</button>
              </form>
           </div>
        </div>
      )}

      {/* Auction Modal */}
      {showAuctionModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                    <Hammer className="h-6 w-6 text-red-600" /> Start Live Auction
                 </h3>
                 <button onClick={() => setShowAuctionModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><X className="h-6 w-6 text-gray-400" /></button>
              </div>
              <form onSubmit={handleCreateAuction} className="p-10 space-y-8">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Product to Auction</label>
                    <select required value={auctionFormData.productId} onChange={e => setAuctionFormData({...auctionFormData, productId: e.target.value})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold">
                       <option value="">Select a product...</option>
                       {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Starting Price ($)</label>
                       <input type="number" required value={auctionFormData.startingPrice} onChange={e => setAuctionFormData({...auctionFormData, startingPrice: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Bid Increment ($)</label>
                       <input type="number" required value={auctionFormData.bidIncrement} onChange={e => setAuctionFormData({...auctionFormData, bidIncrement: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration (Minutes)</label>
                    <input type="number" required value={auctionFormData.durationMinutes} onChange={e => setAuctionFormData({...auctionFormData, durationMinutes: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Live Stream Video URL (Optional)</label>
                    <input value={auctionFormData.videoUrl} onChange={e => setAuctionFormData({...auctionFormData, videoUrl: e.target.value})} placeholder="https://..." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                 </div>
                 <button type="submit" className="w-full py-6 bg-red-600 text-white rounded-[2rem] font-black text-lg shadow-2xl hover:bg-red-700 transition-all">Go Live with Auction</button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminSocialHub;
